/**
 * 🎥 BotLiveView — shared live view for the digit bots (Over 1 & Over 2):
 * 🎲 live digit tape of the market being played (win digits green, losers red)
 * 📊 entries chart — every settled trade as a bar (up = win, down = loss) with
 *    the digit the market printed on it
 * 📜 rich trade history rows
 */
const BotLiveView = ({ digits = [], trades = [], isWinDigit, symbol, testPrefix = "bot" }) => {
  const chron = [...(trades || [])].reverse().slice(-20); // oldest → newest
  const maxAbs = Math.max(0.5, ...chron.map((t) => Math.abs(t.profit || 0)));
  const W = 400, H = 84, mid = H / 2 - 6, bw = W / Math.max(chron.length, 12);
  return (
    <div className="space-y-2">
      {digits.length > 0 && (
        <div data-testid={`${testPrefix}-digit-tape`} className="rounded-md border border-vts-line/40 bg-vts-surface/30 p-2">
          <p className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono mb-1">🎲 Live digits · {symbol || "—"} <span className="normal-case">(green = your winning digits)</span></p>
          <div className="flex gap-1 flex-wrap">
            {digits.map((d, i) => {
              const win = isWinDigit ? isWinDigit(d) : true;
              const last = i === digits.length - 1;
              return (
                <span key={i} className={`w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold font-mono border transition ${win ? "bg-vts-profit/15 border-vts-profit/40 text-vts-profit" : "bg-vts-loss/20 border-vts-loss/50 text-vts-loss"} ${last ? "ring-2 ring-white/60 scale-110" : ""}`}>
                  {d}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {chron.length > 0 && (
        <div data-testid={`${testPrefix}-entries-chart`} className="rounded-md border border-vts-line/40 bg-vts-surface/30 p-2">
          <p className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono mb-1">📊 Bot entries — last {chron.length} trades (bar up = win, down = loss · number = digit printed)</p>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 84 }}>
            <line x1="0" y1={mid} x2={W} y2={mid} stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            {chron.map((t, i) => {
              const p = t.profit || 0;
              const h = Math.max(4, (Math.abs(p) / maxAbs) * (mid - 14));
              const x = i * bw + bw * 0.15;
              const win = p > 0;
              const y = win ? mid - h : mid;
              return (
                <g key={i}>
                  <rect x={x} y={y} width={bw * 0.7} height={h} rx="1.5"
                    fill={win ? "rgba(52,211,153,0.75)" : "rgba(248,113,113,0.75)"}>
                    <title>{`${win ? "+" : ""}$${p.toFixed(2)} · stake $${Number(t.stake || 0).toFixed(2)}${t.digit != null ? ` · digit ${t.digit}` : ""}`}</title>
                  </rect>
                  {t.digit != null && (
                    <text x={x + bw * 0.35} y={win ? y - 3 : y + h + 9} textAnchor="middle"
                      fontSize="8" fontFamily="monospace" fontWeight="bold"
                      fill={win ? "#34d399" : "#f87171"}>{t.digit}</text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      )}
      {(trades || []).length > 0 && (
        <div data-testid={`${testPrefix}-history`} className="rounded-md border border-vts-line/40 bg-vts-surface/25 p-2 space-y-0.5 max-h-44 overflow-y-auto">
          <p className="text-[9px] uppercase tracking-wider text-vts-silver/55 font-semibold font-mono">📜 Trade history</p>
          {(trades || []).slice(0, 15).map((t, i) => {
            const win = (t.profit || 0) > 0;
            const label = t.contract_type === "DIGITOVER" ? `Over ${t.barrier ?? ""}`.trim() : t.contract_type === "DIGITUNDER" ? `Under ${t.barrier ?? ""}`.trim() : (t.contract || t.strategy || "trade");
            return (
              <p key={i} className="text-[9px] font-mono flex items-center gap-1.5">
                <span className={`font-bold w-14 ${win ? "text-vts-profit" : "text-vts-loss"}`}>{win ? "+" : "−"}${Math.abs(t.profit || 0).toFixed(2)}</span>
                <span className="text-vts-silver/60 w-14">{label}</span>
                {t.digit != null && (
                  <span className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold border ${win ? "bg-vts-profit/15 border-vts-profit/40 text-vts-profit" : "bg-vts-loss/20 border-vts-loss/50 text-vts-loss"}`}>{t.digit}</span>
                )}
                <span className="text-vts-silver/45">${Number(t.stake || 0).toFixed(2)} · {t.symbol || ""}</span>
                <span className="text-vts-silver/35 ml-auto">{((t.settled_at || t.created_at || "")).slice(11, 19)}</span>
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BotLiveView;
